/**
 * Floodlight theme store. Since v1.71 Floodlight is dark only (FL_THEMES =
 * ['dark']), so any stored "light" from v1.69 reads as dark. Kept so callers
 * and the attribute contract stay stable if a second theme ever returns.
 *
 *   const [theme, setTheme] = useFlTheme();
 */
import { useSyncExternalStore } from 'react';
import { FL_THEMES, FL_DEFAULT_THEME } from './theme';
import { IS_WEB } from '../../utils/motion';

export const FL_THEME_KEY = 'bo.fl.theme';
const listeners = new Set();

const valid = (t) => (FL_THEMES.includes(t) ? t : null);

function readStored() {
    try {
        return valid(window.localStorage.getItem(FL_THEME_KEY));
    } catch {
        return null;
    }
}

export function getFlTheme() {
    if (!IS_WEB || typeof document === 'undefined') return FL_DEFAULT_THEME;
    return valid(document.documentElement.dataset.flTheme) || readStored() || FL_DEFAULT_THEME;
}

export function setFlTheme(theme) {
    const next = valid(theme) || FL_DEFAULT_THEME;
    if (IS_WEB && typeof document !== 'undefined') {
        const root = document.documentElement;
        // A short cross-fade while the palette swaps (skipped under reduced motion).
        const still = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!still) {
            root.setAttribute('data-fl-theming', '');
            clearTimeout(setFlTheme.t);
            setFlTheme.t = setTimeout(() => root.removeAttribute('data-fl-theming'), 320);
        }
        root.dataset.flTheme = next;
        try {
            window.localStorage.setItem(FL_THEME_KEY, next);
        } catch {
            // Private mode or blocked storage: the choice lasts for this page only.
        }
    }
    listeners.forEach((fn) => fn());
}

function subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

export function useFlTheme() {
    const theme = useSyncExternalStore(subscribe, getFlTheme, () => FL_DEFAULT_THEME);
    return [theme, setFlTheme];
}
